import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpTable, Modal } from "../../components/erp";
import { errorText } from "../../api/client";
import { agentApi, AgentRow, financierApi, FinancierRow, vehicleApi, VehicleRow } from "./api";

/** Shared inline "add" form used by Agents / Vehicles master lists (simple direct-SQL masters, no ApiV2 endpoint in source). */
const AddModal: React.FC<{ title: string; fields: { key: string; label: string }[]; onClose: () => void; onSubmit: (v: Record<string, string>) => Promise<{ Error: string | null }>; testId: string }> = ({ title, fields, onClose, onSubmit, testId }) => {
  const [v, setV] = useState<Record<string, string>>({}); const [err, setErr] = useState<string | null>(null); const [saving, setSaving] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try { const r = await onSubmit(v); if (r.Error) setErr(r.Error); else onClose(); }
    catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title={title} onClose={onClose} testId={testId}>
      <form onSubmit={submit} data-testid={`${testId}-form`}>
        {fields.map((f) => (
          <div className="mb-3" key={f.key}><label className="form-label">{f.label}</label>
            <input className="form-control" value={v[f.key] || ""} onChange={(e) => setV((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`${testId}-${f.key}`} /></div>
        ))}
        {err && <div className="alert alert-danger" data-testid={`${testId}-error`}>{err}</div>}
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid={`${testId}-submit`}>{saving ? "Saving..." : "Save"}</button>
      </form>
    </Modal>
  );
};

/** Port of ApiV2/RmPurchase/RmAgentList.php / RmAgentCreate.php — table RmUsersList (UserType=3). */
export function AgentList() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["purchase", "agents"], queryFn: agentApi.list });
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeader title="Agents" location="Purchase" />
      <div className="card"><div className="card-body">
        <div className="text-center mb-3"><button type="button" className="btn btn-success" onClick={() => setOpen(true)} data-testid="btn-new-agent"><i className="fa fa-plus" /> New Agent</button></div>
        <ErpTable<AgentRow> rows={list.data?.Data || []} loading={list.isLoading} error={list.isError ? errorText(list.error) : list.data?.Error || null} testId="agent-list-table"
          columns={[{ key: "SNO", title: "S.No" }, { key: "UserName", title: "Agent Name" }, { key: "PanNumber", title: "PAN" }, { key: "BankName", title: "Bank" }, { key: "BankAccNo", title: "A/C No" }, { key: "IFSC", title: "IFSC" }, { key: "GstNo", title: "GST" }]} />
      </div></div>
      {open && <AddModal title="New Agent" testId="agent-modal" onClose={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["purchase", "agents"] }); }}
        fields={[{ key: "UserName", label: "Agent Name" }, { key: "PanNumber", label: "PAN" }, { key: "BankName", label: "Bank Name" }, { key: "BankAccNo", label: "Account No" }, { key: "IFSC", label: "IFSC" }, { key: "GstNo", label: "GST No" }]}
        onSubmit={(v) => agentApi.post(v)} />}
    </>
  );
}

/** Port of ApiV2/RmPurchase/RmFinanciersList.php / RmFinancierCreate.php — same table as Suppliers (RmSuppliers). */
export function FinancierList() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["purchase", "financiers"], queryFn: financierApi.list });
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeader title="Financiers" location="Purchase" />
      <div className="card"><div className="card-body">
        <div className="text-center mb-3"><button type="button" className="btn btn-success" onClick={() => setOpen(true)} data-testid="btn-new-financier"><i className="fa fa-plus" /> New Financier</button></div>
        <ErpTable<FinancierRow> rows={list.data?.Data || []} loading={list.isLoading} error={list.isError ? errorText(list.error) : list.data?.Error || null} testId="financier-list-table"
          columns={[{ key: "SNO", title: "S.No" }, { key: "FinancierName", title: "Financier Name" }, { key: "PurchaseCenter", title: "Purchase Center" }, { key: "PanNumber", title: "PAN" }, { key: "BankName", title: "Bank" }, { key: "GstNo", title: "GST" }]} />
      </div></div>
      {open && <AddModal title="New Financier" testId="financier-modal" onClose={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["purchase", "financiers"] }); }}
        fields={[{ key: "FinancierName", label: "Financier Name" }, { key: "PanNumber", label: "PAN" }, { key: "BankName", label: "Bank Name" }, { key: "BankAccNo", label: "Account No" }, { key: "IFSC", label: "IFSC" }, { key: "GstNo", label: "GST No" }]}
        onSubmit={(v) => financierApi.post(v)} />}
    </>
  );
}

/** Port of v3/Purchase/AddVehicle.php / VehicleList.php (direct SQL, no ApiV2 endpoint). */
export function VehicleList() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["purchase", "vehicles"], queryFn: vehicleApi.list });
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeader title="Vehicles" location="Purchase" />
      <div className="card"><div className="card-body">
        <div className="text-center mb-3"><button type="button" className="btn btn-success" onClick={() => setOpen(true)} data-testid="btn-new-vehicle"><i className="fa fa-plus" /> New Vehicle</button></div>
        <ErpTable<VehicleRow> rows={list.data?.Data || []} loading={list.isLoading} error={list.isError ? errorText(list.error) : list.data?.Error || null} testId="vehicle-list-table"
          columns={[{ key: "SNO", title: "S.No" }, { key: "VehicalNumber", title: "Vehicle Number" }, { key: "VehicalCapacity", title: "Capacity" }, { key: "VehicalMake", title: "Make" }]} />
      </div></div>
      {open && <AddModal title="New Vehicle" testId="vehicle-modal" onClose={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["purchase", "vehicles"] }); }}
        fields={[{ key: "VehicalNumber", label: "Vehicle Number" }, { key: "VehicalCapacity", label: "Capacity" }, { key: "VehicalMake", label: "Make" }]}
        onSubmit={(v) => vehicleApi.post(v)} />}
    </>
  );
}
